import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import * as React from 'react'
import type { DateRange } from 'react-day-picker'

export type PeriodKey = 'any' | 'yesterday' | 'week' | 'month' | 'custom'

export interface Period {
  key: PeriodKey
  /** YYYY-MM-DD inclusive; both undefined for 'any'. */
  from?: string
  to?: string
}

/** Same limit as the API (MAX_PERIOD_DAYS) — about 6 months. */
const MAX_RANGE_DAYS = 186

const PRESETS: { key: Exclude<PeriodKey, 'custom'>; label: string }[] = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'any', label: 'Any date' },
]

/** Today in MYT as YYYY-MM-DD. */
function mytToday(): string {
  return new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' })
  ).toLocaleDateString('en-CA')
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** from/to for a preset, in MYT. Weeks start on Monday. */
export function presetPeriod(key: Exclude<PeriodKey, 'custom'>): Period {
  const today = mytToday()
  if (key === 'yesterday') {
    const y = addDays(today, -1)
    return { key, from: y, to: y }
  }
  if (key === 'week') {
    const day = new Date(`${today}T00:00:00Z`).getUTCDay() // 0 = Sunday
    return { key, from: addDays(today, day === 0 ? -6 : 1 - day), to: today }
  }
  if (key === 'month') return { key, from: `${today.slice(0, 8)}01`, to: today }
  return { key: 'any' }
}

function toDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
}

function fmtRange(from: string, to: string): string {
  if (from === to) return format(toDate(from), 'd MMM yyyy')
  return `${format(toDate(from), 'd MMM')} – ${format(toDate(to), 'd MMM yyyy')}`
}

interface PeriodPickerProps {
  value: Period
  onChange: (period: Period) => void
}

/** Preset pills + a custom date-range calendar (max ~6 months). */
export function PeriodPicker({ value, onChange }: PeriodPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<DateRange | undefined>(undefined)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      setDraft(
        value.key === 'custom' && value.from && value.to
          ? { from: toDate(value.from), to: toDate(value.to) }
          : undefined
      )
    }
  }

  const applyDraft = () => {
    if (!draft?.from) return
    const from = format(draft.from, 'yyyy-MM-dd')
    const to = format(draft.to ?? draft.from, 'yyyy-MM-dd')
    onChange({ key: 'custom', from, to })
    setOpen(false)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg bg-muted p-1">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(presetPeriod(p.key))}
            className={cn(
              'rounded-md px-3 py-1.5 text-[12.5px] font-semibold',
              value.key === p.key ? 'bg-card shadow-sm text-text-dark' : 'text-text-muted'
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              'gap-2 font-normal',
              value.key === 'custom' && 'border-text-dark ring-1 ring-text-dark font-semibold'
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {value.key === 'custom' && value.from && value.to
              ? fmtRange(value.from, value.to)
              : 'Custom range'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={draft}
            onSelect={setDraft}
            max={MAX_RANGE_DAYS}
            numberOfMonths={2}
            defaultMonth={draft?.from ?? toDate(addDays(mytToday(), -30))}
            disabled={{ after: toDate(mytToday()) }}
          />
          <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5">
            <span className="text-xs text-text-muted">
              {draft?.from
                ? fmtRange(
                    format(draft.from, 'yyyy-MM-dd'),
                    format(draft.to ?? draft.from, 'yyyy-MM-dd')
                  )
                : 'Pick a start and end date (max 6 months)'}
            </span>
            <Button type="button" size="sm" disabled={!draft?.from} onClick={applyDraft}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
